import { createElement, useState } from 'react';
import { verifiedStarCommandReceipt } from './StarCommandPresentation.js';
const record = (value) => Boolean(value && typeof value === 'object' && !Array.isArray(value));
const text = (value, max = 4096) => typeof value === 'string' && value.length <= max;
const itemId = (value) => typeof value === 'string' && /^[a-f0-9]{24}$/.test(value);
const generationId = (value) => typeof value === 'string' && /^[a-f0-9]{32}$/.test(value);
const requestId = (value) => typeof value === 'string' && /^[a-f0-9-]{36}$/i.test(value);
const appId = (value) => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,95}$/.test(value);
/**
 * An app's icon from the canonical service, or its initial once the image fails to load: Star Command answers its icons
 * with a same-origin resource policy today, which Chromium refuses for a cross-origin image (owner's PC, 2026-10-27: a
 * broken-image glyph on the Hub's card; Star-Command#155). The initial is what a notice without an icon shows anyway.
 */
export function StarCommandAppIcon({ src, initial }) {
    const [failed, setFailed] = useState(false);
    return failed ? createElement('span', null, initial)
        : createElement('img', { src, alt: '', width: 40, height: 40, referrerPolicy: 'no-referrer', onError: () => setFailed(true) });
}
/** The agent's copy of an icon for the page: an image data URL, bounded, keyed by the canonical URL it was fetched from. */
const ICON_DATA_URL = /^data:image\/(?:png|jpeg|webp|gif|svg\+xml);base64,[A-Za-z0-9+/]+=*$/;
const MAX_ICON_DATA_LENGTH = 131_072;
function readIconCopies(value) {
    const copies = new Map();
    if (!record(value))
        return copies;
    for (const [url, data] of Object.entries(value).slice(0, 64)) {
        if (text(url, 2048) && typeof data === 'string' && data.length <= MAX_ICON_DATA_LENGTH && ICON_DATA_URL.test(data))
            copies.set(url, data);
    }
    return copies;
}
/**
 * Host-projected rows contain no destinations or credentials. Icons are limited to the canonical service: its URL, shown
 * through the agent's copy of the bytes where the agent has one (the service's icons refuse a cross-origin load today).
 */
export function readStarCommandSnapshot(value) {
    if (!record(value) || typeof value.enabled !== 'boolean' || typeof value.fresh !== 'boolean'
        || !text(value.revision, 256) || !Number.isSafeInteger(value.unreadCount) || Number(value.unreadCount) < 0
        || !Array.isArray(value.items) || value.items.length > 2000)
        return null;
    const items = [], ids = new Set();
    const copies = readIconCopies(value.icons);
    for (const row of value.items) {
        if (!record(row) || !itemId(row.id) || ids.has(row.id) || !text(row.revision, 256) || !row.revision
            || !appId(row.appId) || !text(row.appName, 256) || !text(row.title, 4096) || !text(row.description, 8192)
            || typeof row.read !== 'boolean' || !text(row.occurredAt, 64) || !Number.isFinite(Date.parse(row.occurredAt))
            || row.effect !== undefined && row.effect !== null && row.effect !== 'celebrate')
            return null;
        ids.add(row.id);
        const initial = row.appName.slice(0, 1);
        let icon = createElement('span', null, initial);
        if (text(row.iconUrl, 2048)) {
            try {
                const url = new URL(row.iconUrl);
                if (url.origin === 'https://starcommand.exante360.com' && !url.username && !url.password)
                    icon = createElement(StarCommandAppIcon, { src: copies.get(row.iconUrl) ?? copies.get(url.href) ?? url.href, initial });
            }
            catch { /* A missing or untrusted icon never changes the notice's text or navigation authority. */ }
        }
        items.push({ id: row.id, revision: row.revision, appId: row.appId, appName: row.appName,
            title: row.title, description: row.description, read: row.read, occurredAt: row.occurredAt, icon,
            ...(row.effect === 'celebrate' ? { effect: row.effect } : {}) });
    }
    return { enabled: value.enabled, fresh: value.fresh, unreadCount: Number(value.unreadCount), revision: value.revision, items };
}
export function readCelebrationStates(value) {
    if (!record(value) || Object.keys(value).length > 2000)
        return null;
    const states = Object.create(null);
    for (const [id, state] of Object.entries(value)) {
        if (!itemId(id) || !record(state) || Object.keys(state).length !== 2 || !['waiting', 'ready', 'playing', 'played'].includes(String(state.state))
            || ![null, 'pc', 'teams', 'reduced-motion'].includes(state.reason)
            || state.state !== 'waiting' && state.reason !== null || state.state === 'waiting' && state.reason === null)
            return null;
        states[id] = { state: state.state, reason: state.reason };
    }
    return states;
}
/** One owner + one native page generation. Abandoned responses cannot affect the next owner's presentation. */
export class StarCommandNativeSession {
    accountKey;
    generation;
    send;
    changed;
    pending = new Map();
    disposed = false;
    refreshing = false;
    snapshotSequence = 0;
    current;
    constructor(accountKey, generation, send, changed) {
        this.accountKey = accountKey;
        this.generation = generation;
        this.send = send;
        this.changed = changed;
        if (!accountKey || !generationId(generation))
            throw new Error('Invalid native owner binding');
        this.current = { enabled: true, fresh: false, accountKey, adapter: {
                accountKey,
                dismiss: async (request, signal) => {
                    const receipt = await this.dismissal('dismiss', request, signal);
                    if (!receipt)
                        throw new Error('Missing dismissal receipt');
                    return receipt;
                },
                receipt: (request, signal) => this.dismissal('receipt', request, signal),
                openNotice: async (notice, signal) => this.navigation('openNotice', { noticeId: notice.id, noticeRevision: notice.revision, appId: notice.appId }, signal),
                openApp: async (id, signal) => this.navigation('openApp', { appId: id }, signal),
                celebrate: notice => {
                    const state = this.current.celebrations?.[notice.id];
                    if (this.disposed || !this.current.enabled || !this.current.fresh || !state || !['waiting', 'ready'].includes(state.state)
                        || state.reason === 'reduced-motion' || !this.current.snapshot?.items.some(item => item.id === notice.id && item.revision === notice.revision && item.effect === 'celebrate'))
                        return;
                    this.send({ type: 'celebration:play', accountKey, generation, inboxItemId: notice.id });
                },
            }, onChanged: () => void this.refresh() };
    }
    get presentation() { return this.current; }
    publish(value) { if (!this.disposed) {
        this.current = value;
        this.changed(value);
    } }
    acceptSnapshot(value) {
        const snapshot = readStarCommandSnapshot(value);
        if (!snapshot) {
            this.publish({ ...this.current, fresh: false, unreadCount: 0 });
            return;
        }
        this.publish({ ...this.current, enabled: snapshot.enabled, fresh: snapshot.fresh,
            unreadCount: snapshot.fresh && snapshot.enabled ? snapshot.unreadCount : 0,
            snapshot: snapshot.enabled && snapshot.fresh ? { accountKey: this.accountKey, revision: snapshot.revision, items: snapshot.items } : undefined });
    }
    receive(value) {
        if (this.disposed || !record(value) || value.accountKey !== this.accountKey || value.generation !== this.generation)
            return;
        if (value.type === 'starcommand:snapshot') {
            this.snapshotSequence++;
            this.acceptSnapshot(value);
            return;
        }
        if (value.type === 'celebration:state') {
            const states = readCelebrationStates(value.items);
            if (states) {
                // A played receipt is terminal for this owner. A delayed host update cannot restore a replay control.
                for (const [id, state] of Object.entries(this.current.celebrations ?? {}))
                    if (state.state === 'played')
                        states[id] = state;
                this.publish({ ...this.current, celebrations: states });
            }
            return;
        }
        if (value.type !== 'starcommand:response' || !requestId(value.requestId))
            return;
        const pending = this.pending.get(value.requestId);
        if (!pending)
            return;
        if (record(value.error)) {
            const status = value.error.status;
            pending.finish(undefined, Object.assign(new Error('Star Command operation was not confirmed'), { status: Number.isInteger(status) && Number(status) >= 400 && Number(status) <= 599 ? Number(status) : 503 }));
        }
        else
            pending.finish(value.result);
    }
    request(operation, fields, signal) {
        if (this.disposed || signal?.aborted || this.pending.size >= 64)
            return Promise.reject(new Error('Native session unavailable'));
        const id = crypto.randomUUID();
        return new Promise((resolve, reject) => {
            const finish = (value, error) => {
                if (!this.pending.delete(id))
                    return;
                clearTimeout(timer);
                signal?.removeEventListener('abort', abort);
                error ? reject(error) : resolve(value);
            };
            const abort = () => {
                try {
                    this.send({ type: 'starcommand:cancel', generation: this.generation, accountKey: this.accountKey, requestId: id });
                }
                catch { /* Native generation replacement also cancels work. */ }
                finish(undefined, new Error('Native operation interrupted'));
            };
            const timer = setTimeout(abort, 14_000);
            this.pending.set(id, { finish });
            signal?.addEventListener('abort', abort, { once: true });
            try {
                this.send({ type: 'starcommand:request', generation: this.generation, accountKey: this.accountKey, requestId: id, operation, ...fields });
            }
            catch {
                abort();
            }
        });
    }
    async refresh() {
        if (this.disposed || this.refreshing)
            return;
        this.refreshing = true;
        const sequence = this.snapshotSequence;
        try {
            const result = await this.request('snapshot', {});
            if (sequence === this.snapshotSequence)
                this.acceptSnapshot(result);
        }
        catch {
            if (sequence === this.snapshotSequence)
                this.publish({ ...this.current, fresh: false, unreadCount: 0 });
        }
        finally {
            this.refreshing = false;
        }
    }
    async dismissal(operation, request, signal) {
        if (request.accountKey !== this.accountKey)
            throw new Error('Native owner changed');
        const result = await this.request(operation, { noticeId: request.noticeId, noticeRevision: request.noticeRevision,
            appId: request.appId, clientRequestId: request.clientRequestId }, signal);
        if (result === null && operation === 'receipt')
            return null;
        if (!record(result) || !verifiedStarCommandReceipt(result, request))
            throw new Error('Unverified Star Command receipt');
        return result;
    }
    async navigation(operation, fields, signal) {
        const result = await this.request(operation, fields, signal);
        if (result !== 'opened' && result !== 'unavailable')
            throw new Error('Unverified navigation result');
        return result;
    }
    dispose() {
        if (this.disposed)
            return;
        this.disposed = true;
        for (const [id, pending] of [...this.pending]) {
            try {
                this.send({ type: 'starcommand:cancel', generation: this.generation, accountKey: this.accountKey, requestId: id });
            }
            catch { /* Native generation replacement also cancels work. */ }
            pending.finish(undefined, new Error('Native owner changed'));
        }
        this.current = { enabled: false, fresh: false, accountKey: this.accountKey };
    }
}
//# sourceMappingURL=StarCommandNative.js.map